import React, { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Crown, BookOpen, Zap } from 'lucide-react';
import { toast } from 'sonner';

const FREE_FEATURES = [
  'Member directory access',
  'Event listings & RSVP',
  'Community news feed',
  'Community forum (read & post)',
  'Direct messaging',
  'Smart networking matches',
  'Sponsor directory',
];

export default function PricingPage({ user }) {
  const [loading, setLoading] = useState(null);
  const [subStatus, setSubStatus] = useState(null);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    fetch('/api/stripe/subscription-status', { credentials: 'include' })
      .then(r => r.json())
      .then(setSubStatus)
      .catch(() => {});
    fetch('/api/stripe/packages')
      .then(r => r.json())
      .then(data => setPackages(data.filter(p => p.type === 'subscription')))
      .catch(() => {});
  }, []);

  const handleCheckout = async (packageId) => {
    setLoading(packageId);
    try {
      const res = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Origin-URL': window.location.origin },
        body: JSON.stringify({ package_id: packageId }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Checkout failed');
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message);
      setLoading(null);
    }
  };

  const isPremium = subStatus?.is_premium;

  const formatPrice = (pkg) => {
    const symbol = pkg.currency === 'gbp' ? '£' : pkg.currency === 'eur' ? '€' : '$';
    const period = pkg.duration_days >= 365 ? '/year' : pkg.duration_days >= 28 ? '/month' : `/${pkg.duration_days} days`;
    return { symbol, amount: Math.round(pkg.amount) === pkg.amount ? pkg.amount : pkg.amount.toFixed(2), period };
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold font-heading mb-3 gradient-text">
              Membership Plans
            </h1>
            <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto">
              Unlock premium content, courses, and keynote slides to accelerate your sustainability journey.
            </p>
          </div>

          {isPremium && (
            <div data-testid="active-subscription-banner" className="mb-8 p-4 rounded-xl bg-brand-sage/10 border border-brand-sage/30 text-center">
              <p className="text-brand-dark font-semibold flex items-center justify-center gap-2">
                <Crown className="h-5 w-5" /> You have an active Premium subscription
                {subStatus?.subscription_tier && (
                  <span className="text-sm font-normal ml-1">
                    ({subStatus.subscription_tier.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())})
                  </span>
                )}
              </p>
              {subStatus?.subscription_expires_at && (
                <p className="text-sm text-brand-sage mt-1">
                  Expires: {new Date(subStatus.subscription_expires_at).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          <div className={`grid gap-8 ${packages.length === 0 ? 'md:grid-cols-2' : packages.length <= 2 ? 'md:grid-cols-3' : `md:grid-cols-${Math.min(packages.length + 1, 4)}`}`}>
            {/* Free Plan */}
            <Card data-testid="plan-free" className="p-8 border-2 border-slate-200 relative">
              <div className="mb-6">
                <h3 className="text-lg font-bold font-heading text-slate-800 mb-1">Free</h3>
                <p className="text-sm text-slate-500">Community basics</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">£0</span>
                <span className="text-slate-500 ml-1">/forever</span>
              </div>
              <ul className="space-y-3 mb-8">
                {FREE_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="h-4 w-4 text-brand-sage mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button data-testid="plan-free-btn" variant="outline" className="w-full" disabled>
                Current Plan
              </Button>
            </Card>

            {/* Dynamic Plans */}
            {packages.map((pkg, idx) => {
              const price = formatPrice(pkg);
              const isPopular = idx === 0;
              const isBestValue = pkg.duration_days >= 365;

              return (
                <Card key={pkg.id} data-testid={`plan-${pkg.id}`}
                  className={`p-8 border-2 relative ${isPopular ? 'border-brand-blue shadow-lg shadow-brand-blue/20' : isBestValue ? 'border-brand-green' : 'border-slate-200'}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-brand-navy text-white text-xs font-semibold rounded-full">
                      Most Popular
                    </div>
                  )}
                  {isBestValue && !isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-brand-sage text-white text-xs font-semibold rounded-full">
                      Best Value
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className="text-lg font-bold font-heading text-slate-800 mb-1">{pkg.name}</h3>
                    {pkg.description && <p className="text-sm text-slate-500">{pkg.description}</p>}
                  </div>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900">{price.symbol}{price.amount}</span>
                    <span className="text-slate-500 ml-1">{price.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {(pkg.features?.length > 0 ? pkg.features : ['All premium features included']).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isPopular ? 'text-brand-navy' : 'text-brand-sage'}`} /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    data-testid={`plan-${pkg.id}-btn`}
                    onClick={() => handleCheckout(pkg.id)}
                    disabled={!!loading || isPremium}
                    className="w-full bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white"
                  >
                    {loading === pkg.id ? (
                      <span className="flex items-center gap-2"><Zap className="h-4 w-4 animate-spin" /> Redirecting...</span>
                    ) : isPremium ? 'Active' : (
                      pkg.duration_days >= 365 ? 'Subscribe Annually' : 'Subscribe Now'
                    )}
                  </Button>
                </Card>
              );
            })}
          </div>

          {/* Individual Course Purchase Info */}
          <div className="mt-12 text-center">
            <Card data-testid="course-purchase-info" className="inline-block p-6 border border-slate-200">
              <div className="flex items-center gap-3">
                <BookOpen className="h-6 w-6 text-brand-navy" />
                <div className="text-left">
                  <p className="font-semibold text-slate-800">Not ready for a subscription?</p>
                  <p className="text-sm text-slate-500">
                    Purchase individual courses for <strong>£49 each</strong> from the Courses page.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
